
import { defineComponent, PropType } from 'vue';
import { dispatchToast } from '@/utils/dispatchToast';
import {
  VTextField,
  VDataTable,
} from 'vuetify/components';

import type { UpdateSaleLineReqBody } from '@/services/pricingHistories.service';
import { SaleLineDetailsForDataTable, VuetifyDataTableHeader } from '@/utils/helpers';
import LinePricingInfo from '@/components/shared/LinePricingInfo.vue';
import SuggestedPriceEditor from '@/components/views/saleOrderDetails/SuggestedPriceEditor.vue';
import AppDialog from '@/components/AppDialog.vue';
import { useIsMobile } from '@/composables/useIsMobile';

export default defineComponent({
  name: 'VuetifyDataTable',
  setup() {
    return { isMobile: useIsMobile() };
  },
  components: {
    VDataTable,
    VTextField,
    LinePricingInfo,
    SuggestedPriceEditor,
    AppDialog,
  },
  props: {
    columnsDefs: {
      type: Array as () => VuetifyDataTableHeader[],
      required: true,
      default: () => [],
    },
    rowsData: {
      type: Array as () => SaleLineDetailsForDataTable[],
      required: true,
      default: () => [],
    },
    class: {
      type: String,
      default: '',
    },
    updateRowFn: {
      type: Function,
      required: true,
    },
    itemKey: {
      type: String,
      required: true,
    },
    // When set, clickable cells (itemKey column) render a real <a href> so
    // Cmd/Ctrl-click and middle-click open the target in a new tab natively.
    clickableCellHrefFn: {
      type: Function as PropType<(item: SaleLineDetailsForDataTable) => string>,
      default: null,
    },
    editableColumns: {
      type: Array,
      default: () => [],
    },
    textareaColumns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: '',
      // Live window height for calcTableHeight (see resize listener below).
      windowHeight: window.innerHeight,
      // Measured distance from the viewport top to the table (navbar +
      // header card + optional banner + search). Sane default until the
      // first measurement lands in mounted().
      tableTop: 320,
      isEditing: false, // State to control the dialog visibility
      editedValue: null as string | null, // To hold the edited value temporarily
      editedColumnKey: null as string | null, // To know which column is being edited
      editedItem: null as SaleLineDetailsForDataTable | null,
      // Dedicated suggested-price editor (static / base+adder formula).
      isPriceEditorOpen: false,
      priceEditorItem: null as SaleLineDetailsForDataTable | null,
      priceEditorLoading: false,
      groupBy: [{ key: 'familyCode', order: 'asc' }],
      sortBy: [{ key: 'partNumber', order: 'asc' }],
      customKeySort: {
        // alphanumeric string sorting for part numbers
        partNumber: (pn1: string, pn2: string) => {
          return pn1.localeCompare(pn2, 'en', { numeric: true });
        },
      },
    };
  },

  mounted() {
    // Table height follows the window (calcTableHeight), like the RFQs grid.
    window.addEventListener('resize', this.onWindowResize);
    this.$nextTick(this.measureTableTop);

    // Defer group expansion to avoid blocking UI
    setTimeout(() => {
      // This workaround is needed to fix the issue with the Vuetify DataTable of not having expanded by default
      const buttons = document.querySelectorAll('.v-data-table-group-header-row button.group-toggle-btn');

      // Expand groups progressively in batches
      this.expandGroupsProgressively(Array.from(buttons), 0);

      // Re-measure once layout has settled (fonts, banner, wrapping).
      this.measureTableTop();
    }, 100);
  },
  updated() {
    // Content above the table can appear/resize after mount (e.g. the
    // multiple-pricelists banner); the guarded assignment inside
    // measureTableTop prevents update loops.
    this.measureTableTop();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  computed: {
    isDataTableVisible() {
      return this.columnsDefs.length && this.rowsData.length;
    },
    editDialogTitle() {
      const column = this.columnsDefs.find((columnDef) => columnDef.key === this.editedColumnKey);
      return column?.title ? `Edit ${column.title}` : 'Edit';
    },
    filteredColumnsDefs() {
      return this.columnsDefs
        .filter((columnDef) => columnDef.key !== 'familyCode')
        // Centre the Suggested Price header over its (wide) price → formula column.
        .map((columnDef) =>
          columnDef.key === 'suggestedPrice' ? { ...columnDef, align: 'center' } : columnDef,
        ) as VuetifyDataTableHeader[];
    },
    calcTableHeight() {
      // Size to the actual window, not the monitor (`screen.height` overshoots
      // on smaller laptops where the browser window is shorter than the
      // display). Clamped so short windows still get a usable scroll area.
      // `windowHeight` is kept fresh by a resize listener, so the table
      // grows/shrinks with the window (like the RFQs grid) instead of being
      // frozen at its mount-time size.
      if (this.isMobile) {
        // Phones: hand-tuned reserve, unchanged (incl. the extra bottom
        // clearance that keeps the floating hamburger off the last row).
        return Math.max(320, this.windowHeight - 230);
      }
      // Desktop: the bottom edge lands 16px above the window bottom — the
      // same visible gap as the RFQs grid (its wrapper's mb-4). Derived
      // from the table's measured viewport top (see measureTableTop), not a
      // hand-tuned constant, so it holds whether or not the pricelists
      // banner is present or the header card wraps.
      return Math.max(320, this.windowHeight - this.tableTop - 16);
    },
  },
  methods: {
    onWindowResize() {
      this.windowHeight = window.innerHeight;
      this.measureTableTop();
    },
    measureTableTop() {
      const el = this.$el?.querySelector?.('.so-lines-table');
      if (!el) return;
      // The page scrolls inside `.contentArea`, not the window, so a raw
      // getBoundingClientRect().top is short by however far it is scrolled.
      // Adding scrollTop back gives the resting distance, which is what the
      // height is derived from: without it, any re-render while scrolled (a
      // keystroke in the search box) stores a smaller top and inflates the
      // table a little further each time.
      const scroller = this.$el?.closest?.('.contentArea');
      const top = Math.round(el.getBoundingClientRect().top + (scroller?.scrollTop ?? 0));
      // Only assign on real change so the updated() hook can call this
      // without re-render loops (height change doesn't move the top).
      if (top > 0 && top !== this.tableTop) this.tableTop = top;
    },
    isEditable(columnValue) {
      return this.editableColumns.includes(columnValue);
    },
    isTextareaColumn(columnKey) {
      return this.textareaColumns.includes(columnKey);
    },
    isClickable(columnKey) {
      return columnKey === this.itemKey;
    },
    getCellClass(columnDef) {
      const classes: string[] = [];
      if (this.isEditable(columnDef.key)) classes.push('editable-cell');
      else if (this.isClickable(columnDef.key)) classes.push('clickable-cell');
      // The Suggested Price cell renders the wide price → formula layout and
      // needs extra right padding so the edit pencil clears the formula.
      if (columnDef.key === 'suggestedPrice') classes.push('suggested-price-cell');
      return classes.join(' ');
    },

    onCellClick(rowIndex: number, columnDef: VuetifyDataTableHeader, item: SaleLineDetailsForDataTable, event?: MouseEvent) {
      // Check if we clicked on the lock icon
      if (event?.target && (event.target as HTMLElement).closest('[data-action="unlock-price"]')) {
        this.$emit('unlock-price', item);
        return;
      }
      
      if (this.isClickable(columnDef.key)) {
        // Modified clicks (Cmd/Ctrl/Shift) on the cell link keep the native
        // anchor behavior — open in a new tab/window.
        if (
          event &&
          (event.metaKey || event.ctrlKey || event.shiftKey) &&
          (event.target as HTMLElement).closest('a.cell-link')
        ) {
          return;
        }
        // Plain click: cancel the anchor's full-page navigation and route
        // through the SPA handler instead.
        event?.preventDefault();
        this.emitCellClicked(columnDef, item);
        return;
      }
      if (!this.isEditable(columnDef.key)) {
        return;
      }
      // The suggested price gets a richer editor (static price or base+adder
      // formula); other editable columns keep the generic inline dialog.
      if (columnDef.key === 'suggestedPrice') {
        // Lines matching multiple pricelists: a general cell click opens the
        // pricelist selector (choosing a pricelist is the primary action);
        // only a click on the pencil zone opens the manual price editor.
        if (this.hasMultiplePriceLists(item) && !this.isPencilZoneClick(event)) {
          this.$emit('select-pricelist', item);
          return;
        }
        this.priceEditorItem = item;
        this.isPriceEditorOpen = true;
        return;
      }
      this.startEditing(rowIndex, columnDef, item);
    },

    hasMultiplePriceLists(item: SaleLineDetailsForDataTable) {
      return (item.multiplePriceListIds?.length ?? 0) > 0;
    },

    // The edit pencil is a CSS ::after glyph inside the cell's reserved right
    // padding (40px on suggested-price cells), so it can't receive clicks
    // itself — instead, treat any click landing in that padding band as a
    // "pencil" click.
    isPencilZoneClick(event?: MouseEvent) {
      if (!event?.currentTarget) return false;
      const cellRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      return cellRect.right - event.clientX <= 40;
    },

    async onPriceEditorSave(payload: UpdateSaleLineReqBody) {
      if (!this.priceEditorItem) return;
      this.priceEditorLoading = true;
      const { success, data } = await this.updateRowFn(this.priceEditorItem.id, payload);
      this.priceEditorLoading = false;

      if (success) {
        this.$emit('update-item', {
          columnKey: 'suggestedPrice',
          value: payload.suggestedPrice,
          item: this.priceEditorItem,
          data,
        });
        this.isPriceEditorOpen = false;
      } else {
        dispatchToast(`Failed to update the price: ${data.message}`, {
          type: 'error',
          timeout: false,
          draggable: false,
          closeOnClick: false,
        });
      }
    },

    emitCellClicked(columnDef, item) {
      this.$emit('cellClicked', {
        colDef: {
          field: columnDef.key,
        },
        data: item,
      });
    },
    cancelEdit() {
      this.isEditing = false;
    },
    async saveEdit() {
      const columnKey = this.editedColumnKey;
      const newValue = this.editedValue;

      // Construct the request body
      const reqBody = {
        [columnKey]: newValue,
      };

      const { success, data } = await this.updateRowFn(this.editedItem.id, reqBody);

      if (success) {
        // Update the local data after a successful update on the server
        this.$emit('update-item', {
          columnKey: this.editedColumnKey,
          value: this.editedValue,
          item: this.editedItem,
          data: data,
        });
      } else {
        dispatchToast(`Failed to update the cell: ${data.message}`, {
          type: 'error',
          timeout: false,
          draggable: false,
          closeOnClick: false,
        });
      }

      this.isEditing = false;
    },
    startEditing(rowIndex: number, columnDef: VuetifyDataTableHeader, item: SaleLineDetailsForDataTable) {
      this.editedItem = item;
      this.editedColumnKey = columnDef.key;
      this.editedValue = item[columnDef.key];
      this.isEditing = true;

      // After the Vue component has been updated, select the input text
      this.$nextTick(() => {
        if (this.isTextareaColumn(columnDef.key)) {
          if (this.$refs.editTextarea && this.$refs.editTextarea.$el) {
            this.$refs.editTextarea.$el.querySelector('textarea').focus();
          }
        } else {
          if (this.$refs.editInput && this.$refs.editInput.$el) {
            this.$refs.editInput.$el.querySelector('input').select();
          }
        }
      });
    },
    truncateContent(content, length = 30) {
      if (content && content.length > length) {
        return `${content.substring(0, length)}...`;
      }
      return content;
    },
    formatCurrency(value) {
      if (value !== null && value !== undefined && !isNaN(parseFloat(value))) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(parseFloat(value));
      }
      return '';
    },
    formatPercentage(value) {
      if (value !== null && value !== undefined) {
        return `${parseFloat(value).toFixed(2)}%`;
      }
      return '';
    },
    formattedValue(columnDef, value, item) {
      const fallbackValue = item[columnDef.fallbackColumn];
      const isValueEmpty = value === '' || value == null;
      const displayValue = isValueEmpty && fallbackValue != null ? fallbackValue : value;
      const isFallback = isValueEmpty && fallbackValue != null;

      let formattedValue = '';

      // Special handling for odooPrice column
      if (columnDef.key === 'odooPrice') {
        formattedValue = this.formatCurrency(displayValue);
        const shouldBeGrey = formattedValue !== this.formatCurrency(item.suggestedPrice || item.enginePrice);
        formattedValue = shouldBeGrey ? `<span class="disabled">${formattedValue}</span>` : formattedValue;
        return item.isPriced ? `${formattedValue} <i class="mdi mdi-lock lock-icon" data-id="${item.id}" data-action="unlock-price"></i>` : formattedValue;
      }

      switch (columnDef.formatter) {
        case 'currency':
          formattedValue = this.formatCurrency(displayValue);
          break;
        case 'percentage':
          formattedValue = this.formatPercentage(displayValue);
          break;
        default:
          formattedValue = displayValue;
          break;
      }

      // Show placeholder for suggestedPrice when no price but multiple pricelists available
      if (columnDef.key === 'suggestedPrice' && !formattedValue && item.enginePriceType === 'multiple_pricelist_pending') {
        return `<span class="fallback">$ --.-- &nbsp</span>`
      }

      return isFallback ? `<span class="fallback">${formattedValue}</span>` : formattedValue;
    },
    getTooltipText(columnDef, item) {
      const value = item[columnDef.key];
      const fallbackValue = item[columnDef.fallbackColumn];
      const isValueEmpty = value === '' || value == null;
      const isFallbackValueEmpty = fallbackValue === '' || fallbackValue == null;

      // For suggestedPrice column, check if there's a direct tooltip (for multiple pricelist cases)
      if (columnDef.key === 'suggestedPrice' && !item.suggestedPrice && item.enginePriceTooltip) {
        return item.enginePriceTooltip;
      }

      if (isValueEmpty && !isFallbackValueEmpty && columnDef.fallbackTooltip) {
        return item[columnDef.fallbackTooltip];
      }

      if (columnDef.key === 'odooPrice' && item.isPriced) {
        return `This price has already been posted, posting it again will have no effect. <br>If the price needs to be modified, click on the lock icon to unlok it.`;
      }
      return '';
    },
    // Render the shared LinePricingInfo component for priced lines. The
    // multiple-pricelist workflow states (pending placeholder / selected with a
    // richer tooltip) keep their bespoke string rendering below.
    shouldUsePricingInfo(item: SaleLineDetailsForDataTable) {
      return (
        !!item.pricingInfo &&
        item.pricingInfo.price !== null &&
        item.enginePriceType !== 'multiple_pricelist_selected'
      );
    },
    getIcon(item) {
      switch (item.enginePriceType) {
        case 'multiple_pricelist_selected':
          return 'mdi-tag-check-outline';
        case 'multiple_pricelist_pending':
          return 'mdi-tag-multiple-outline';
        case 'price_list':
          return 'mdi-tag-text-outline';
        case 'exact':
          return 'mdi-equal';
        case 'series':
          return 'mdi-playlist-check';
        case 'cost':
          return 'mdi-cash-multiple';
        default:
          return '';
      }
    },
    expandGroupsProgressively(buttons, startIndex) {
      // If we've processed all buttons, we're done
      if (startIndex >= buttons.length) {
        return;
      }

      // Process buttons in small batches (2 at a time)
      const batchSize = 2;
      const endIndex = Math.min(startIndex + batchSize, buttons.length);

      for (let i = startIndex; i < endIndex; i++) {
        (buttons[i] as HTMLElement).click();
      }

      // Schedule next batch with a small delay
      setTimeout(() => this.expandGroupsProgressively(buttons, endIndex), 50);
    },

  },
});
